import * as XLSX from 'xlsx';

export const normalizeImportHeader = (header) => (
  String(header || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '')
);

const HEADER_ALIASES = {
  glcode: 'glCode',
  gl_code: 'glCode',
  code: 'glCode',
  gldescription: 'glDescription',
  gl_description: 'glDescription',
  description: 'glDescription',
  desc: 'glDescription',
  glaccount: 'glAccount',
  gl_account: 'glAccount',
  account: 'glAccount',
  glsystem: 'glSystem',
  gl_system: 'glSystem',
  system: 'glSystem',
  accounttype: 'accountType',
  account_type: 'accountType',
  type: 'accountType',
  status: 'status',
};

export const mapImportRow = (rawRow) => {
  const mapped = {};
  Object.entries(rawRow || {}).forEach(([key, value]) => {
    const normalizedKey = normalizeImportHeader(key);
    const targetKey = HEADER_ALIASES[normalizedKey] || normalizedKey;
    if (value == null) return;
    const text = String(value).trim();
    if (!text) return;
    mapped[targetKey] = text;
  });
  return mapped;
};

export const parseSpreadsheetFile = async (file) => {
  if (!file) return [];

  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: 'array', cellDates: false });
  const sheetName = workbook.SheetNames[0];
  if (!sheetName) return [];

  const sheet = workbook.Sheets[sheetName];
  const rawRows = XLSX.utils.sheet_to_json(sheet, { defval: '' });
  return rawRows.map(mapImportRow).filter((row) => Object.keys(row).length > 0);
};

export const buildGlAccountBulkPayload = (rows, tenantId, options = {}) => {
  const defaultGlSystem = options.defaultGlSystem || 'MANUAL';
  const propertyId = options.propertyId || null;
  const nowIso = new Date().toISOString();

  return rows
    .map((row) => {
      const glCode = String(row.glCode || '').trim();
      const glDescription = String(row.glDescription || '').trim();
      if (!glCode || !glDescription) return null;

      const glAccount = String(row.glAccount || '').trim() || `${glCode}-${glDescription}`;
      const glSystem = String(row.glSystem || defaultGlSystem).trim() || defaultGlSystem;
      const status = String(row.status || 'ACTIVE').trim().toUpperCase() || 'ACTIVE';
      const rowPropertyId = row.propertyId || propertyId || null;

      return {
        tenantId,
        propertyId: rowPropertyId,
        glCode,
        glDescription,
        glAccount,
        glSystem,
        accountType: row.accountType ? String(row.accountType).trim() : null,
        status,
        glAuditModifiedDatetime: nowIso,
      };
    })
    .filter(Boolean);
};

export const formatChartOfAccountBulkMessage = (body) => {
  if (!body) return 'Import completed.';
  if (body.message) return body.message;
  const created = body.createdCount ?? 0;
  const updated = body.updatedCount ?? 0;
  const saved = body.savedCount ?? (created + updated);
  const skipped = body.skippedCount ?? 0;
  return `Saved ${saved} (${created} created, ${updated} updated); skipped ${skipped}.`;
};

export const GL_ACCOUNT_MASTER_IMPORT_COLUMNS = [
  {
    fieldKey: 'glCode',
    label: 'GL Code',
    expectedHeaders: ['GL Code', 'GL_CODE', 'gl_code', 'Code'],
    required: true,
  },
  {
    fieldKey: 'glDescription',
    label: 'Description',
    expectedHeaders: ['Description', 'GL Description', 'GL_DESCRIPTION', 'gl_description'],
    required: true,
  },
  {
    fieldKey: 'glAccount',
    label: 'GL Account',
    expectedHeaders: ['GL Account', 'GL_ACCOUNT', 'gl_account', 'Account'],
    required: false,
  },
  {
    fieldKey: 'glSystem',
    label: 'GL System',
    expectedHeaders: ['GL System', 'GL_SYSTEM', 'gl_system', 'System'],
    required: false,
  },
  {
    fieldKey: 'accountType',
    label: 'Account Type',
    expectedHeaders: ['Account Type', 'ACCOUNT_TYPE', 'account_type', 'Type'],
    required: false,
  },
];

const resolveFieldKeyFromHeader = (header) => {
  const normalized = normalizeImportHeader(header);
  return HEADER_ALIASES[normalized] || normalized;
};

export const extractSpreadsheetHeaders = async (file) => {
  if (!file) return [];

  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: 'array', cellDates: false });
  const sheetName = workbook.SheetNames[0];
  if (!sheetName) return [];

  const sheet = workbook.Sheets[sheetName];
  const rawRows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });
  const headerRow = rawRows[0] || [];
  return headerRow.map((h) => String(h ?? '').trim()).filter(Boolean);
};

export const buildImportColumnMatchRows = (fileHeaders, columnSpecs) => {
  const headerEntries = (fileHeaders || [])
    .map((h) => ({
      original: String(h || '').trim(),
      fieldKey: resolveFieldKeyFromHeader(h),
    }))
    .filter((entry) => entry.original);

  return (columnSpecs || []).map((spec) => {
    const match = headerEntries.find((entry) => entry.fieldKey === spec.fieldKey);
    return {
      fieldKey: spec.fieldKey,
      label: spec.label,
      expectedHeader: (spec.expectedHeaders && spec.expectedHeaders[0]) || spec.label,
      required: spec.required === true,
      matched: Boolean(match),
      displayFileCol: match?.original || 'Missing',
    };
  });
};

export const getImportHeaderMandatoryMessage = (matchRows) => {
  const missing = (matchRows || []).filter((row) => row.required && !row.matched);
  if (!missing.length) return null;
  const names = missing.map((row) => row.expectedHeader).join(', ');
  return `Row 1 must include required columns: ${names}.`;
};
